import { Autocomplete } from '@mui/material';
import { AxiosResponse } from 'axios';
import { memo, useMemo, useCallback, useEffect, useRef } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { Operator } from 'shell/models/operator';
import { ItemVM } from 'shell/view-models/item';
import { Operation } from 'shell/view-models/operation';
import debounce from 'shell/utils/debounce';
import PmiTextField from 'shell/components/shared/text-field';

export const idAllOperators = 'ALL';
export const idNoneSelected = '0';

interface OperatorListComponentProps {
  disabled?: boolean;
  value: string;
  onChange: (operator?: string, operatorObj?: ItemVM | null, items?: Array<ItemVM>) => void;
  error?: boolean;
  label?: string;
  id?: string;
  idService?: string;
  required?: boolean;
  operatorType?: string;
  fgCashManagement?: boolean;
  auth?: string;
  defaultListValue?: ItemVM;
  isAnpr?: boolean;
  allOperator?: boolean;
}

const OperatorListComponent = ({
  disabled,
  idService,
  fgCashManagement,
  isAnpr,
  operatorType,
  auth,
  onChange,
  required,
  value,
  error,
  defaultListValue,
  allOperator = true,
  label = 'Operatore',
  id = 'operator-list',
}: OperatorListComponentProps): React.ReactElement => {
  const ref = useRef<string | null>(null);
  const [operator, getOperators] = useHttpFetch<HttpResponseList<Operator>, Array<ItemVM>>(
    `${__MANAGEMENT_API_URL__}/operatori`,
    {
      onBootstrap: false,
      initialData: [],
      options: {
        loading: false,
        errorCatching: true,
      },
      transformer: (response: AxiosResponse<HttpResponseList<Operator>>) => {
        const { data } = response;

        return data?.payload.list.map((item) => ({
          id: item.key.codiceOperatore,
          value: item.cognomeNome,
          operator: item,
        })) as Array<ItemVM>;
      },
    }
  );

  useEffect(() => {
    if (idService || fgCashManagement || ref.current || operatorType) {
      getOperators({
        params: {},
        paramsSerializer: () =>
          `filters=nominativo:${ref.current || ''}:${Operation.LIKE}${
            idService && idService !== 'ALL' && idService !== 'TUTTI'
              ? `&filters=codiceServizio:${idService}:${Operation.EQUALS}`
              : ''
          }${fgCashManagement ? `&filters=gestioneCassa:true` : ''}${
            operatorType ? `&filters=tipoOperatore:${operatorType}` : ''
          }${isAnpr ? `&filters=ANPR:true` : ''}`,
      });
    }
  }, [idService, fgCashManagement, operatorType, isAnpr]);

  const options = useMemo(() => {
    let items = (operator.data as Array<ItemVM>)?.slice() || [];

    if (idService !== 'ALL' || auth === '0') {
      if (idService !== 'TUTTI' && allOperator) {
        items.push({
          id: idAllOperators,
          value: 'Tutti gli operatori',
        });
      }
    }

    if (defaultListValue) {
      items = [defaultListValue, ...items];
    }

    return items.filter((item) =>
      idService === 'ALL' || idService === 'TUTTI' ? item.id !== 'ALL' : true
    );
  }, [operator.data, idService, auth, defaultListValue]);

  const handleSearch = debounce<string>(
    useCallback(
      (searchValue) => {
        if (operator.abort) {
          operator.abort();
        }

        ref.current = searchValue;

        getOperators({
          params: {},
          paramsSerializer: () =>
            `filters=nominativo:${searchValue}:${Operation.LIKE}${
              idService && idService !== 'ALL' && idService !== 'TUTTI'
                ? `&filters=codiceServizio:${idService}:${Operation.EQUALS}`
                : ''
            }${fgCashManagement ? `&filters=gestioneCassa:true` : ''}${
              operatorType
                ? `&filters=tipoOperatore:${operatorType}${isAnpr ? `&filters=ANPR:true` : ''}`
                : ''
            }`,
        });
      },
      [getOperators, operator.abort, idService, operatorType, isAnpr]
    )
  );

  const selectedValue = useMemo(() => {
    if (value) {
      if (options && options.length > 0) {
        return options.find((elem) => {
          return String(elem.id) === String(value);
        });
      }
      return null;
    }
    return null;
  }, [value, options]);

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option.value}
      id={id}
      value={selectedValue || null}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      onChange={(event, element) => {
        event.stopPropagation();
        onChange((element?.id as string) || '', element, options);
      }}
      disabled={disabled}
      onInputChange={(event, changeValue, reason) => {
        if (changeValue.length >= 3 && reason === 'input') {
          handleSearch(changeValue);
        }
      }}
      clearOnBlur={false}
      loading={operator.isLoading}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );
};

OperatorListComponent.displayName = 'Operator List component';

const OperatorList = memo(OperatorListComponent);

export default OperatorList as typeof OperatorListComponent;
